import { refreshSignedUrlIfNeeded, linkMediaToSourceMediaEvent, updateMediaStatusWithRequestInProgressCheck, updateMediaEventIds, updateMedia } from '../media/actions';
import { MediaType, CameraType } from '../media/index';

export const makeEventIdWithUnixTimestamp = (event) => {
    if (!event) return;

    const getUnixTimestamp = (dateString) => {
        // Parse the date string and treat it as local time
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            throw new Error("Invalid date string");
        }

        // Adjust for local timezone offset to get the correct UTC timestamp
        const localTimestamp = date.getTime(); // Local time in milliseconds
        const timezoneOffset = date.getTimezoneOffset() * 60 * 1000; // Offset in milliseconds
        return Math.floor((localTimestamp - timezoneOffset) / 1000); // Convert to Unix timestamp
    };

    if (event.timestamp) {
        return `${getUnixTimestamp(event.timestamp)}-${event.type}`;
    }
    if (event.ts) {
        return `${getUnixTimestamp(event.ts)}-${event.type}`;
    }

    return;
};

export const makeEventId = (event) => {
    if (!event) return;
    // original functionality:
    if (event.timestamp) {
        return `${event.timestamp.toJSON()}${event.type}`;
    }
    // patched functionality to leverage this function elsewhere in the app (event to upgrade event IDs to support duplicate events on the same timestampt)
    if (event.ts) { // assumption, event.ts is a date string with timezone
        return `${event.ts}${event.type}`;
    }
    return;
}
const makeEventIdFromLinkedEventId = (linkedEventId) => `${linkedEventId.timestamp.toISOString()}${linkedEventId.type}`;

const getEventFromState = (ravenUuid, eventId, getState) => {
    return getState().data.events.eventMap.getIn([ravenUuid, eventId]);
}

export const ACTION_TYPES = { // maps to the reducers (reminder: reducers only update the redux store)
    // update the eventMap
    ADD_ALL_EVENTS: 'ADD_ALL_EVENTS',
    // specifically, update an event in the eventMap
    UPDATE_EVENT: 'UPDATE_EVENT', 
    ADD_MEDIA_ID_TO_EVENT: 'ADD_MEDIA_ID_TO_EVENT',
    CLEAR_REQUEST_EVENT_USER_VIDEO_FROM_RAVEN: 'CLEAR_REQUEST_EVENT_USER_VIDEO_FROM_RAVEN',
    REQUEST_EVENT_USER_VIDEO_FROM_RAVEN: 'REQUEST_EVENT_USER_VIDEO_FROM_RAVEN',
    REQUEST_EVENT_USER_VIDEO_FROM_RAVEN_UPDATE_STATUS: 'REQUEST_EVENT_USER_VIDEO_FROM_RAVEN_UPDATE_STATUS',
    REQUEST_EVENT_USER_VIDEO_FROM_RAVEN_ERROR: 'REQUEST_EVENT_USER_VIDEO_FROM_RAVEN_ERROR',
    REQUEST_EVENT_USER_VIDEO_FROM_RAVEN_SUCCESS: 'REQUEST_EVENT_USER_VIDEO_FROM_RAVEN_SUCCESS',  // DOES NOTHING. Event action clearRequestEventUserVideoFromRaven() triggered by updateEventAndDependentMedia())
}

export const LOCAL_ONLY_EVENT_TYPES = {
    EVENT_MEDIA_INITIATED_WEBAPP_TIMEOUT: "EVENT_MEDIA_INITIATED_WEBAPP_TIMEOUT",
    EVENT_MEDIA_STATUS_UPDATE_WEBAPP_TIMEOUT: "EVENT_MEDIA_STATUS_UPDATE_WEBAPP_TIMEOUT",
    EVENT_MEDIA_WEBAPP_MQTT_ERROR: "EVENT_MEDIA_WEBAPP_MQTT_ERROR"
}

export const REQUEST_UPDATE_TIMEOUTS = {
    MAX_TIME_UNTIL_MEDIA_INITIATED_MS: 20000,
    MAX_TIME_BETWEEN_MEDIA_STATUS_UPDATES: 120000
}

// basic/core action creators, corresponding to ACTION_TYPES above, kinda like helpers
const addAllEvents = (ravenUuid, eventList) => ({
    type: ACTION_TYPES.ADD_ALL_EVENTS,
    payload: { ravenUuid, eventList }
});

const updateEvent = (ravenUuid, event) => ({
    type: ACTION_TYPES.UPDATE_EVENT,
    payload: { ravenUuid, event }
});

export const addMediaIdToEvent = (ravenUuid, eventId, mediaId) => ({ // exported because also used by 'media' actions
    type: ACTION_TYPES.ADD_MEDIA_ID_TO_EVENT,
    payload: { ravenUuid, eventId, mediaId }
});

const clearRequestEventUserVideoFromRaven = (ravenUuid, eventId) => ({
    type: ACTION_TYPES.CLEAR_REQUEST_EVENT_USER_VIDEO_FROM_RAVEN,
    payload: {ravenUuid, eventId}
});

const requestEventUserVideoFromRaven = (ravenUuid, assignedEventId, request, timeoutId) => ({

    type: ACTION_TYPES.REQUEST_EVENT_USER_VIDEO_FROM_RAVEN,
    payload: {
        ravenUuid,
        eventId: assignedEventId, // request property (protobuf EventId) has the same required properties as an Event
        request,
        timeoutId
    }
});

const requestEventUserVideoFromRavenUpdateStatus = (ravenUuid, assignedEventId, statusEventType, statusEventTimestamp, statusUpdateTimeoutId) => ({

    type: ACTION_TYPES.REQUEST_EVENT_USER_VIDEO_FROM_RAVEN_UPDATE_STATUS,
    payload: {
        ravenUuid,
        eventId: assignedEventId, // request property (protobuf EventId) has the same required properties as an Event
        statusEventType,
        statusEventTimestamp,
        statusUpdateTimeoutId
    }
});

const requestEventUserVideoFromRavenSuccess = (ravenUuid, eventId) => ({
    type: ACTION_TYPES.REQUEST_EVENT_USER_VIDEO_FROM_RAVEN_SUCCESS,
    payload: {
        ravenUuid,
        eventId
    }
});

export const requestEventUserVideoFromRavenError = (ravenUuid, eventId, statusEventType, error) => ({
    type: ACTION_TYPES.REQUEST_EVENT_USER_VIDEO_FROM_RAVEN_ERROR,
    payload: {
        ravenUuid,
        eventId,
        statusEventType,
        error
    }
});


export const addAllEventsAndLinkMedia = (ravenUuid, eventList) => dispatch => {
    eventList.forEach(event => {
        if(!event.eventId) event.eventId = makeEventId(event);
    });
    dispatch(addAllEvents(ravenUuid, eventList));
    if(eventList.length) {
        dispatch(updateMediaEventIds(ravenUuid, eventList));
    }
    return Promise.resolve();
};

const getRemainingCameraForUserMediaRequest = (currentRequestImmutable) => {

    const currentRequestSentForCameraType = currentRequestImmutable.get('requestSentForCameraType');

    if (!currentRequestSentForCameraType) {
        console.warn("User media request re-sent for event without a requestSentForCameraType. getRemainingCameraForUserMediaRequest returns null.");
        return null;
    }
    if (!currentRequestSentForCameraType.get(CameraType.CAMERA_ROAD)) {
        return CameraType.CAMERA_ROAD;
    }
    if (!currentRequestSentForCameraType.get(CameraType.CAMERA_CABIN)) {
        return CameraType.CAMERA_CABIN;
    }
    return null;
}

export const updateEventAndDependentMedia = (ravenUuid, event, mqttService) => (dispatch, getState) => {

    if (!event.eventId) {
        event.eventId = makeEventId(event); // 'makeEventId' works because 'event' is from the cloud/mqtt, it is not 'immutable' type
    }

    dispatch(updateEvent(ravenUuid, event));
    if (event.linkedEventId && event.linkedEventId !== 0 && event.linkedEventId !== "0") {
        const linkedEventId = makeEventIdFromLinkedEventId(event.linkedEventId);

        if (!linkedEventId) {
            console.warn("Events actions updateEventAndDependentMedia invalid linked_event_id: " + JSON.stringify(event));
        } else {
            event.mediaIds.forEach(mediaId =>
                dispatch(
                    linkMediaToLinkedEventId(ravenUuid, mediaId, linkedEventId, addMediaIdToEvent)
                ).catch(error => console.warn(error.message))
            );
            let assignedEvent = getEventFromState(ravenUuid, linkedEventId, getState);
            if (!assignedEvent) {
                console.warn("Event actions updateEventAndDependentMedia assignedEvent not found in store");
            } else {
                let eventUserMediaRequest = assignedEvent.get('userMediaRequest'); // "let" because refreshing eventUserMediaRequest on error
                if (eventUserMediaRequest) {
                    if (event.mediaIds && event.type && event.type.startsWith('EVENT_MEDIA_')) {

                        if (eventUserMediaRequest.get('request')) {
                            if (eventUserMediaRequest.get('timeoutId')) {
                                clearTimeout(eventUserMediaRequest.get('timeoutId'))
                            }

                            // TODO update 'request's requestSentForCameraType upon confirmation (I skipped because it is a relatively major PITA because redux)

                            switch (event.type) {
                                case "EVENT_MEDIA_UPLOAD_FAILED":
                                case "EVENT_MEDIA_SHORT_FAILED":
                                case "EVENT_MEDIA_RECORDING_FAIL":
                                case "EVENT_MEDIA_DOWNLOAD_FAILED":
                                case "EVENT_MEDIA_FAILED":
                                    dispatch(requestEventUserVideoFromRavenError(
                                        ravenUuid,
                                        linkedEventId,
                                        event.type,
                                        new Error(event.type)
                                    ));                        
                                    break;
                                case "EVENT_MEDIA_UPLOAD_COMPLETED": // Component using redux state updates must handle createEventUserVideo call because it has mqttService
                                    const remainingCameraForUserMediaRequest = getRemainingCameraForUserMediaRequest(eventUserMediaRequest.get('request'));
                                    if (remainingCameraForUserMediaRequest) {
                                        dispatch(createEventUserVideo(
                                            mqttService,
                                            ravenUuid,
                                            remainingCameraForUserMediaRequest,
                                            linkedEventId
                                        ));
                                        dispatch(requestEventUserVideoFromRavenUpdateStatus(
                                            ravenUuid,
                                            linkedEventId,
                                            event.type,
                                            event.timestamp,
                                            null // no status update timeout, all done
                                        ));
                                    } else {
                                        dispatch(clearRequestEventUserVideoFromRaven(
                                            ravenUuid,
                                            linkedEventId
                                        ));
                                    }
                                    break;
                                default:
                                    //console.log("Event actions updateEventAndDependentMedia default/continuing case for event type " + event.type);
                                    dispatch(requestEventUserVideoFromRavenUpdateStatus(
                                        ravenUuid,
                                        linkedEventId,
                                        event.type,
                                        event.timestamp,
                                        setTimeout(() => {
                                            dispatch(requestEventUserVideoFromRavenError(
                                                ravenUuid,
                                                linkedEventId,
                                                LOCAL_ONLY_EVENT_TYPES.EVENT_MEDIA_STATUS_UPDATE_WEBAPP_TIMEOUT,
                                                new Error("timeout waiting for update after " + event.type + " in createUserVideo")
                                            ));
                                        }, REQUEST_UPDATE_TIMEOUTS.MAX_TIME_BETWEEN_MEDIA_STATUS_UPDATES)
                                    ));
                            }
                        }
                    }
                }
            }
        }
        return Promise.resolve(event);


    // DEPRECATED_VIDEO_PREVIEW_REQUEST
    } else if (event.eventParams) {
        const sourceMediaId = event.eventParams.EVENT_PARAM_SOURCE_MEDIA_ID;
        if (sourceMediaId) {
            event.mediaIds.forEach(mediaId =>
                dispatch(
                    linkMediaToSourceMediaEvent(
                        ravenUuid,
                        mediaId,
                        `${sourceMediaId}`,
                        addMediaIdToEvent
                    )
                ).catch(err => console.warn(err.message))
            );
        }
    }

    if (event.mediaIds && event.type && event.type.startsWith('EVENT_MEDIA_')) {
        event.mediaIds.forEach(id =>
            dispatch(
                updateMediaStatusWithRequestInProgressCheck(
                    ravenUuid,
                    id,
                    event.type,
                    event.timestamp
                )
            ).catch(err => console.warn(err.message))
        );
    }
    return Promise.resolve(event);
};

export const requestImageUrlsForEventIfNeeded = (ravenUuid, eventId) => (dispatch, getState) => {
    const event = getState().data.events.eventMap.getIn([ravenUuid, eventId]);
    if(!event) {
        return Promise.reject(new Error(`event ${ravenUuid}.${eventId} not found in store`));
    }
    return Promise.all(event.get('mediaIds').toSeq().map(mId =>
        dispatch(refreshSignedUrlIfNeeded(ravenUuid, mId))
        .catch(err => console.warn(err)) // don't give up on any failures
    ));
};

export const clearEventUserVideoRequest = (ravenUuid, assignedEventId) => (dispatch, getState) => {
    dispatch(clearRequestEventUserVideoFromRaven(
        ravenUuid,
        assignedEventId
    ));
};

const PREVIEW_VIDEO_DURATION_S = 10;
const PREVIEW_VIDEO_START_OFFSET_FROM_EVENT_S = -5;

export const createEventUserVideo = (mqttService, ravenUuid, cameraType, assignedEventId) => (dispatch, getState) => {

    if (!mqttService) return Promise.reject(new Error('mqttService is required by createUserVideo'));
    if (!ravenUuid) return Promise.reject(new Error('ravenUuid is required by createUserVideo'));
    if (!assignedEventId) return Promise.reject(new Error('assignedEventId is required by createUserVideo'));

    let assignedEvent = getEventFromState(ravenUuid, assignedEventId, getState); // "let" because refreshing assignedEvent on error
    if (!assignedEvent) return Promise.reject(new Error(`event ${ravenUuid}.${assignedEventId} not found in store during createUserVideo request `));
    const assignedEventTimestamp = assignedEvent.get('timestamp');
    if (!assignedEventTimestamp) return Promise.reject(new Error(`event ${ravenUuid}.${assignedEventId} with invalid timestamp during createUserVideo request`));
    const assignedEventType = assignedEvent.get('type');
    if (!assignedEventType) return Promise.reject(new Error(`event ${ravenUuid}.${assignedEventId} with invalid type during createUserVideo request`));

    let eventUserMediaRequest = assignedEvent.get('userMediaRequest'); // "let" because refreshing eventUserMediaRequest on error

    let requestSentForCameraType = {} // local only, used by updateEventAndDependentMedia, not used in mqttService's send media request

    if (eventUserMediaRequest && eventUserMediaRequest.get('request')) {
        // check if request can be re-sent
        const requestStatus = eventUserMediaRequest.get('statusEventType');
        if (!requestStatus) {
            // if no status yet, the initial request is still in progress and Raven has not replied (nor timeout)
            console.log(`A user media request is currently in progress for event ${ravenUuid}.${assignedEventId}`);
            return Promise.resolve();
        }

        const currentRequest = eventUserMediaRequest.get('request');
        const currentRequestSentForCameraType = currentRequest.get('requestSentForCameraType');
        if (!currentRequestSentForCameraType) {
            console.warn("User media request re-sent for event without a requestSentForCameraType. Setting both known cameras to true to avoid assumed endless loop.");
            requestSentForCameraType[CameraType.CAMERA_ROAD] = true;
            requestSentForCameraType[CameraType.CAMERA_CABIN] = true;
        } else {
            requestSentForCameraType[CameraType.CAMERA_ROAD] = currentRequestSentForCameraType.get(CameraType.CAMERA_ROAD);
            requestSentForCameraType[CameraType.CAMERA_CABIN] = currentRequestSentForCameraType.get(CameraType.CAMERA_CABIN);
        }
    } else {
        requestSentForCameraType[CameraType.CAMERA_ROAD] = false;
        requestSentForCameraType[CameraType.CAMERA_CABIN] = false;    
    }

    requestSentForCameraType[cameraType] = true; // TODO updateEventAndDependentMedia should update 'request's requestSentForCameraType upon confirmation (I skipped because it is a relatively major PITA because redux)

    const mediaRequest = {
        mediaType: MediaType.MEDIA_PREVIEW_VIDEO,
        cameraType: cameraType,  // eventMediaConstants.EVENT_MEDIA_CAMERA_TYPE or CameraType from ./index (they are the same)
        requestSentForCameraType: requestSentForCameraType,
        duration: PREVIEW_VIDEO_DURATION_S,
        eventId: {
            timestamp: assignedEventTimestamp,
            type: assignedEventType
        },
        startOffset: PREVIEW_VIDEO_START_OFFSET_FROM_EVENT_S
    }

    const mediaRequestTimeoutId = setTimeout(() => {
            dispatch(requestEventUserVideoFromRavenError(
                ravenUuid,
                assignedEventId,
                LOCAL_ONLY_EVENT_TYPES.EVENT_MEDIA_INITIATED_WEBAPP_TIMEOUT,
                new Error('timeout waiting for EVENT_MEDIA_INITIATED in createUserVideo')
            ));
        }, REQUEST_UPDATE_TIMEOUTS.MAX_TIME_UNTIL_MEDIA_INITIATED_MS
    );

    // update redux state
    dispatch(requestEventUserVideoFromRaven(ravenUuid, assignedEventId, mediaRequest, mediaRequestTimeoutId));

    // send the request via mqtt
    return mqttService.sendMediaRequest({ ravenUuid, request: mediaRequest }).then(() => {

        dispatch(requestEventUserVideoFromRavenSuccess(ravenUuid, assignedEventId));

    }).catch(error => {

        console.error(error);

        assignedEvent = getEventFromState(ravenUuid, assignedEventId, getState); 

        if (assignedEvent) {
            eventUserMediaRequest = assignedEvent.get('userMediaRequest'); // "let" because refreshing eventUserMediaRequest on error
            if (eventUserMediaRequest && eventUserMediaRequest.get('timeoutId')) {
                // prevent the timeout from clobbering an mqtt error
                clearTimeout(eventUserMediaRequest.get('timeoutId'));
            }
        }
        dispatch(requestEventUserVideoFromRavenError(
            ravenUuid,
            assignedEventId,
            LOCAL_ONLY_EVENT_TYPES.EVENT_MEDIA_WEBAPP_MQTT_ERROR,
            error
        ));
    });
};

export const linkMediaToLinkedEventId = (ravenUuid, mediaId, linkedEventId, addMediaIdToEvent) => (dispatch, getState) => {
    dispatch(updateMedia(ravenUuid, { mediaId, eventIds: [linkedEventId] }));
    addMediaIdToEvent(ravenUuid, linkedEventId, mediaId);
    return Promise.resolve();
};